import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, Subject, throwError, timer } from 'rxjs';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { catchError } from 'rxjs/operators';
import { DASHBOARD_FILTERS_STORAGE_KEY } from '@partner/core/const/session-storage-keys';
import { ConfigService, getSafeOAuthSessionTimeoutMs } from 'common';

@Injectable({
  providedIn: 'root'
})
export class OAuthService {
  constructor(
    protected configService: ConfigService,
    protected router: Router,
    protected http: HttpClient) {
  }

  static oauthSessionTimeout: any;

  // Static: `common`'s OAuthService token resolves to a second instance of this class.
  private static readonly sessionEndedSubject: Subject<void> = new Subject<void>();

  /** Emits after every local session end; not on `sessionBegin`, which also runs on token refresh. */
  readonly sessionEnded$: Observable<void> = OAuthService.sessionEndedSubject.asObservable();

  get accessToken(): string {
    return localStorage.token;
  }

  transformRequest(obj): string {
    const str = [];
    for (const p in obj)
      str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
    return str.join("&");
  }

  refreshAuthToken(): Observable<any> {
    if (!localStorage.refreshToken) {
      this.sessionEndLocal();
      return of(null);
    }
    const body = this.transformRequest({
      grant_type: "refresh_token",
      client_id: localStorage.loginType,
      refresh_token: localStorage.refreshToken
    });
    return this.http.post(`${this.configService.environment.oauth.url}/token`, body, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" }
    }).pipe(
      // Only a rejected refresh (4xx) ends the session; network and 5xx errors keep it. Every error is rethrown.
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status >= 400 && err.status < 500) {
          this.sessionEndLocal();
        }
        return throwError(() => err);
      }),
    );
  }

  get authenticated(): boolean {
    const tokenExpiresAt = localStorage.tokenExpiresAt
      ? new Date(localStorage.tokenExpiresAt)
      : undefined;
    const tokenExpired = tokenExpiresAt
      && tokenExpiresAt.getTime() < new Date().getTime();

    return this.accessToken && tokenExpiresAt && !tokenExpired;
  }

  sessionBegin(loginType: string, accessToken: string, expiresIn: number, refreshToken: string, refresh_token_expires_in?: number, clientId?: string): void {
    const now = new Date();

    localStorage.loginType = loginType;
    localStorage.token = accessToken;
    localStorage.tokenSetAt = now;
    localStorage.tokenExpiresAt = new Date(now.getTime() + expiresIn * 1000);
    localStorage.refreshToken = refreshToken;

    if (clientId) {
      localStorage.clientId = clientId;
    }

    if (refresh_token_expires_in) {
      localStorage.refreshTokenExpiresAt = new Date(now.getTime() + refresh_token_expires_in * 1000);
    }

    this.restartIdleTimer();
  }

  sessionEndLocal(): void {
    this.sessionTimeoutClear();
    localStorage.removeItem('loginType');
    localStorage.removeItem('token');
    localStorage.removeItem('tokenSetAt');
    localStorage.removeItem('tokenExpiresAt');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('refreshTokenExpiresAt');

    sessionStorage.removeItem('navigationHistory')
    sessionStorage.removeItem(DASHBOARD_FILTERS_STORAGE_KEY);

    OAuthService.sessionEndedSubject.next();
  }

  protected sessionTimeoutClear(): void {
    if (OAuthService.oauthSessionTimeout && OAuthService.oauthSessionTimeout.unsubscribe) {
      OAuthService.oauthSessionTimeout.unsubscribe();
    }

    OAuthService.oauthSessionTimeout = null;
  }
  restartIdleTimer(): void {
    this.sessionTimeoutClear();
    sessionStorage.removeItem("sessionExpiredAutoLogout");

    if (!localStorage.tokenExpiresAt || !localStorage.refreshToken) return;

    const refreshToken = jwtDecode<JwtPayload>(localStorage.refreshToken);

    const signOutDueTimeInMs = getSafeOAuthSessionTimeoutMs({
      accessTokenExpiresAtMs: new Date(localStorage.tokenExpiresAt).getTime(),
      refreshTokenExpiresAtMs: refreshToken.exp * 1000,
    });

    OAuthService.oauthSessionTimeout = timer(signOutDueTimeInMs).subscribe(() => {
      this.sessionEndLocal();
      sessionStorage.setItem("sessionExpiredAutoLogout", "true");
      this.router.navigate(["sign-out"], { replaceUrl: true });
    });

  }

  saveNavigationHistory(): void {
    if (window.location.pathname !== "/mfa") {
      const params = new URLSearchParams(window.location.search);
      params.delete("sessionId");
      sessionStorage.setItem("navigation.history", window.location.pathname);
      sessionStorage.setItem(
        "navigation.historyQuery",
        `${window.location.pathname}?${params.toString()}`
      );
    }
  }
}
